/**
 * Automatic shipping-rate estimate for logged-in customers with a
 * saved default address — fetches rates via Shopify's cart shipping-
 * rate endpoints without requiring the customer to type anything in,
 * since the address is already known.
 *
 * HONESTY NOTE, seriously meant: this uses
 * /cart/prepare_shipping_rates.json and /cart/async_shipping_rates.json,
 * a real but much less commonly used corner of Shopify's Ajax API
 * compared to /cart/add.js or /cart.js. The prepare step kicks off an
 * async calculation; the rates aren't ready immediately, so this polls
 * the second endpoint until it gets a final answer (a 202 status means
 * "still calculating, try again shortly"). The exact response shape
 * and timing here are based on best available understanding, not
 * something verified against a live store from this environment — of
 * everything built in this whole project, this file is the most likely
 * to need real debugging once it's actually tested against your store.
 *
 * That's why every failure path just hides the estimate section rather
 * than showing an error or partial/wrong data. If this breaks, the
 * customer simply doesn't see a shipping estimate — they still see
 * accurate shipping at actual checkout either way, since Shopify
 * calculates that independently regardless of whether this preview
 * worked.
 */
(function () {
  var MAX_POLL_ATTEMPTS = 6;
  var POLL_DELAY_MS = 1000;

  // NOTE: /cart/async_shipping_rates.json returns each rate's `price` as a
  // STRING already in decimal dollars (e.g. "8.00"), not cents — confirmed
  // directly against the live store. formatMoney takes that decimal string
  // (or a number) and formats it as-is; it does NOT divide by 100, unlike
  // the cart-add/toast formatMoney helpers elsewhere in this theme, which
  // do work in cents. Mixing the two conventions up is exactly what broke
  // this originally (rates rendered ~100x too low).
  function formatMoney(decimalPrice) {
    return '$' + parseFloat(decimalPrice).toFixed(2);
  }

  function hideEstimate(container) {
    container.hidden = true;
  }

  function renderRates(container, rates) {
    var loadingEl = container.querySelector('[data-shipping-loading]');
    var resultsEl = container.querySelector('[data-shipping-results]');

    if (!rates || !rates.length) {
      hideEstimate(container);
      return;
    }

    if (loadingEl) loadingEl.hidden = true;

    // rate.price is a decimal string ("8.00", "15.00") — comparing those
    // as strings picks the lexicographically smallest, not the numerically
    // cheapest (e.g. "15.00" < "8.00" is true as a string). Must compare
    // as numbers.
    var cheapest = rates.reduce(function (min, rate) {
      return parseFloat(rate.price) < parseFloat(min.price) ? rate : min;
    }, rates[0]);

    var html =
      '<p class="shipping-estimate__intro">' +
      rates.length +
      (rates.length === 1 ? ' shipping option available, starting at ' : ' shipping options available, starting at ') +
      formatMoney(cheapest.price) +
      '</p><ul class="shipping-estimate__list">';

    rates.forEach(function (rate) {
      html += '<li><span>' + rate.name + '</span><span>' + formatMoney(rate.price) + '</span></li>';
    });
    html += '</ul>';

    if (resultsEl) {
      resultsEl.innerHTML = html;
      resultsEl.hidden = false;
    }
  }

  function pollRates(container, attempt) {
    fetch('/cart/async_shipping_rates.json')
      .then(function (res) {
        if (res.status === 202) {
          if (attempt < MAX_POLL_ATTEMPTS) {
            setTimeout(function () {
              pollRates(container, attempt + 1);
            }, POLL_DELAY_MS);
            return null;
          }
          throw new Error('Shipping rates did not finish calculating in time');
        }
        if (!res.ok) {
          throw new Error('async_shipping_rates.json returned ' + res.status);
        }
        return res.json();
      })
      .then(function (data) {
        if (!data) return; // still polling, handled above
        renderRates(container, data.shipping_rates);
      })
      .catch(function (err) {
        console.error('Shipping rate estimate failed:', err);
        hideEstimate(container);
      });
  }

  function init() {
    var container = document.querySelector('[data-shipping-estimate]');
    if (!container) return;

    var address = {
      zip: container.getAttribute('data-zip'),
      country: container.getAttribute('data-country'),
      province: container.getAttribute('data-province'),
    };

    if (!address.zip || !address.country) {
      hideEstimate(container);
      return;
    }

    fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shipping_address: address }),
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('prepare_shipping_rates.json returned ' + res.status);
        }
        pollRates(container, 0);
      })
      .catch(function (err) {
        console.error('Shipping rate prepare failed:', err);
        hideEstimate(container);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
